import React, { useState, useEffect, useMemo } from "react";
import {
  FileText,
  ChevronDown,
  ChevronRight,
  Target,
  Calendar,
  Activity,
  CreditCard,
  CheckCircle2,
  Circle,
  Edit,
  Trash2,
  TrendingUp,
  TrendingDown,
  Minus,
  User,
  ExternalLink,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  TreatmentPlan,
  getGoalStatusColor,
  getGoalStatusLabel,
  getVitalTypeLabel,
  getVitalTypeUnit,
  getBillingFrequencyLabel,
  getTargetDirectionSymbol,
} from "@/lib/treatmentPlan/treatmentPlanData";
import { cn } from "@/lib/utils";
import { Appointment } from "@/lib/dashboard/appointmentsData";
import { Consultation } from "@/lib/dashboard/consultationsData";
import { useTitanApp } from "@/hooks/useTitanApp";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { TreatmentPlanModal } from "../../../../TreatmentPlan/TreatmentPlanModal";
import {
  getPatientActiveTreatmentPlan,
  deleteTreatmentPlanAction,
} from "../../../../../../../dispatchers/treatmentPlanDispatchers";
import { useScheduleData } from "@/components/Calendar/scheduleData";

interface ConsultationActiveTreatmentPlansProps {
  treatmentPlans: any[];
  isDarkMode: boolean;
  isLoading?: boolean;
  linkedTreatmentPlanId?: string | null;
  linkedTreatmentPlanAppointmentId?: string | null;
  onViewDetails?: (plan: TreatmentPlan) => void;
  onEditTreatmentPlan?: (plan: TreatmentPlan) => void;
  appointments?: Appointment[];
  consultations?: Consultation[];
  vitals?: any[];
  onVitalClick?: (vitalType: string) => void;
}

export function ConsultationActiveTreatmentPlans({
  treatmentPlans,
  isDarkMode,
  isLoading = false,
  linkedTreatmentPlanId,
  linkedTreatmentPlanAppointmentId,
  onViewDetails,
  onEditTreatmentPlan,
  appointments = [],
  consultations = [],
  vitals = [],
  onVitalClick,
}: ConsultationActiveTreatmentPlansProps) {
  const { pharmacyServiceState, patientState } = useTitanApp();
  const { defaultPharmacyServices = [] } = pharmacyServiceState || {};
  const { selectedPatientDetails, consultationId: currentConsultationId } =
    patientState || {};
  const [expandedPlans, setExpandedPlans] = useState<Set<string>>(new Set());
  const [selectedAppointmentConsultation, setSelectedAppointmentConsultation] =
    useState<Consultation | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<any>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingPlan, setDeletingPlan] = useState<any>(null);
  const { servicePharmacists, serviceTypesInfo, serviceRooms } =
    useScheduleData();
  const getServiceNameById = (serviceId?: string) => {
    if (!serviceId) return "";

    const service = (defaultPharmacyServices || []).find(
      (s: any) => String(s?._id) === String(serviceId),
    );

    return service?.serviceName || service?.name || "";
  };

  const normalizeGoalVitalType = (type?: string) => {
    if (!type) return type;
    const normalized = String(type).toLowerCase();
    const map: Record<string, string> = {
      blood_pressure: "blood-pressure",
      heart_rate: "heart-rate",
      blood_glucose: "blood-glucose",
      oxygen_saturation: "oxygen-saturation",
      respiratory_rate: "respiratory-rate",
      news_pain_score: "news-pain-score",
    };
    return map[normalized] ?? normalized;
  };

  const mapEventStatus = (eventStatus: number): Appointment["status"] => {
    switch (eventStatus) {
      case 1:
        return "Completed";
      case 2:
        return "Pending";
      case 3:
        return "Active";
      case 4:
        return "Rescheduled";
      default:
        return "Pending";
    }
  };

  const formatAppointmentFromApi = (appt: any): any => {
    const startDate = appt.startDate ? new Date(appt.startDate) : null;
    const date = startDate
      ? startDate.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "";
    const time = appt.startDate ? appt.startDate.substring(11, 16) : "";

    return {
      id: appt.id || appt._id || "",
      date,
      time,
      type:
        getServiceNameById(appt.serviceID || appt.serviceId) ||
        appt.serviceName ||
        appt.serviceType ||
        appt.name ||
        "Appointment",
      status: mapEventStatus(appt.eventStatus),
      practitionerName: appt.resourceId || appt.pharmacist || "",
      patientConsultaitonId: appt.consultationID || appt.consultationId || "",
      patientID: appt.patientID || appt.patientId || "",
      eventStatus: appt.eventStatus,
    };
  };

  const parseAppointmentDate = (appointment?: Partial<Appointment> | null) => {
    if (!appointment?.date) return Number.POSITIVE_INFINITY;

    const [day, month, year] = appointment.date.split("/");
    if (!day || !month || !year) return Number.POSITIVE_INFINITY;

    const normalizedYear = year.length === 2 ? `20${year}` : year;
    const parsedDate = new Date(
      `${normalizedYear}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`,
    );

    return Number.isNaN(parsedDate.getTime())
      ? Number.POSITIVE_INFINITY
      : parsedDate.getTime();
  };

  const transformedPlans = useMemo(
    () =>
      (treatmentPlans || []).map((plan: any) => ({
        ...plan,
        __original: plan,
        id: String(plan.treatmentPlanId ?? plan.id ?? plan._id ?? ""),
        status:
          ({ 1: "active", 2: "completed", 3: "paused", 4: "cancelled" } as any)[
            plan.status
          ] ?? String(plan.status || "").toLowerCase(),
        goals: (plan.goals ?? []).map((goal: any, idx: number) => ({
          ...goal,
          id: String(goal.id ?? goal._id ?? `${idx}`),
          linkedVitalType: (() => {
            if (goal.linkedVitalType == null) return undefined;
            const mapped = (() => {
              const numericMap: Record<number, string> = {
                1: "weight",
                2: "blood_pressure",
                3: "heart_rate",
                4: "temperature",
                5: "height",
                6: "bmi",
                7: "oxygen_saturation",
                8: "blood_glucose",
              };

              const stringMap: Record<string, string> = {
                weight: "weight",
                bloodpressure: "blood_pressure",
                blood_pressure: "blood_pressure",
                heartrate: "heart_rate",
                heart_rate: "heart_rate",
                temperature: "temperature",
                height: "height",
                bmi: "bmi",
                oxygensaturation: "oxygen_saturation",
                oxygen_saturation: "oxygen_saturation",
                bloodglucose: "blood_glucose",
                blood_glucose: "blood_glucose",
              };

              if (typeof goal.linkedVitalType === "number") {
                return (
                  numericMap[goal.linkedVitalType] ??
                  String(goal.linkedVitalType)
                );
              }

              const key = String(goal.linkedVitalType)
                .toLowerCase()
                .replace(/\s+/g, "_");
              return stringMap[key] ?? key;
            })();
            return normalizeGoalVitalType(mapped);
          })(),
          targetDirection:
            typeof goal.targetDirection === "number"
              ? (({ 1: "AtOrAbove", 2: "AtOrBelow", 3: "EqualTo" } as any)[
                  goal.targetDirection
                ] ?? "AtOrBelow")
              : (goal.targetDirection ?? undefined),
          status:
            (
              {
                1: "pending",
                2: "in-progress",
                3: "achieved",
                4: "abandoned",
              } as any
            )[goal.status] ?? String(goal.status || "pending").toLowerCase(),
        })),
        followUpAppointments: plan.followUpAppointments ?? [],
        linkedPrescriptions: plan.linkedPrescriptions ?? [],
        consultationHistory: plan.consultationHistory ?? [],
        subscriptionPlan: plan.subscriptionPlan ?? undefined,
      })),
    [treatmentPlans],
  );

  // Auto-expand linked treatment plan
  useEffect(() => {
    if (linkedTreatmentPlanId) {
      setExpandedPlans((prev) => {
        const newSet = new Set(prev);
        newSet.add(String(linkedTreatmentPlanId));
        return newSet;
      });
    }
  }, [linkedTreatmentPlanId]);

  // Filter for active plans only
  const activePlans = transformedPlans.filter(
    (plan) => plan.status === "active",
  );

  // Helper to get appointment details from IDs or full objects
  const getAppointmentDetails = (appointmentData: any[]) => {
    if (!appointmentData || appointmentData.length === 0) return [];

    const appts = appointmentData.map((item: any) => {
      if (typeof item === "string" || typeof item === "number") {
        return appointments.find((apt) => String(apt?.id) === String(item));
      }
      return formatAppointmentFromApi(item);
    });

    return appts
      .filter(Boolean)
      .sort((a, b) => parseAppointmentDate(a) - parseAppointmentDate(b));
  };

  if (isLoading) {
    return (
      <div className="mt-6">
        <div
          className={cn(
            "rounded-lg border p-4 text-sm",
            isDarkMode
              ? "bg-gray-800 border-gray-700 text-gray-400"
              : "bg-white border-gray-200 text-gray-600",
          )}
        >
          Loading treatment plans...
        </div>
      </div>
    );
  }

  if (activePlans.length === 0) {
    return null;
  }

  const togglePlan = (planId: string) => {
    setExpandedPlans((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(planId)) {
        newSet.delete(planId);
      } else {
        newSet.add(planId);
      }
      return newSet;
    });
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "active":
        return isDarkMode
          ? "bg-green-900/30 text-green-400 border-green-800"
          : "bg-green-100 text-green-800 border-green-200";
      case "paused":
        return isDarkMode
          ? "bg-yellow-900/30 text-yellow-400 border-yellow-800"
          : "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "completed":
        return isDarkMode
          ? "bg-blue-900/30 text-blue-400 border-blue-800"
          : "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return isDarkMode
          ? "bg-gray-700 text-gray-300 border-gray-600"
          : "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText
            className={`w-4 h-4 ${isDarkMode ? "text-purple-400" : "text-purple-600"}`}
          />
          <h3
            className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            Active Treatment Plans
          </h3>
          <Badge
            variant="secondary"
            className={cn(
              "text-xs",
              isDarkMode
                ? "bg-gray-700 text-gray-300"
                : "bg-gray-100 text-gray-700",
            )}
          >
            {activePlans.length}
          </Badge>
        </div>
      </div>

      <div className="space-y-2">
        {activePlans.map((plan) => {
          const isExpanded = expandedPlans.has(plan.id);
          const daysRemaining = plan.endDate
            ? Math.ceil(
                (new Date(plan.endDate).getTime() - new Date().getTime()) /
                  (1000 * 60 * 60 * 24),
              )
            : null;

          // Calculate goal stats
          const totalGoals = plan.goals?.length || 0;
          const achievedGoals =
            plan.goals?.filter((g) => g.status === "achieved").length || 0;
          const goalsWithVitals =
            plan.goals?.filter((g) => g.linkedVitalType) || [];

          const isLinkedPlan =
            !!linkedTreatmentPlanId &&
            String(linkedTreatmentPlanId) === String(plan.id);

          return (
            <div
              key={plan.id}
              className={cn(
                "rounded-lg border transition-colors",
                isDarkMode
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200",
                isLinkedPlan &&
                  (isDarkMode
                    ? "ring-2 ring-purple-600 border-purple-600"
                    : "ring-2 ring-purple-500 border-purple-500"),
              )}
            >
              {/* Linked Consultation Notice */}
              {isLinkedPlan && (
                <div
                  className={cn(
                    "px-3 pt-3 pb-0",
                    isDarkMode ? "bg-purple-900/10" : "bg-purple-50",
                  )}
                >
                  <div className="flex items-center gap-2 p-2 rounded-md bg-gradient-to-r from-purple-600 to-purple-700 text-white">
                    <div className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                    </div>
                    <span className="text-xs font-semibold">
                      Active Consultation Linked to This Plan
                    </span>
                  </div>
                </div>
              )}

              {/* Plan Header */}
              <div
                className={cn(
                  "p-3 cursor-pointer hover:bg-opacity-80 transition-colors",
                  isDarkMode ? "hover:bg-gray-750" : "hover:bg-gray-50",
                )}
                onClick={() => togglePlan(plan.id)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-500" />
                    ) : (
                      <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-500" />
                    )}
                    <div className="flex-1 min-w-0">
                      <h4
                        className={cn(
                          "text-sm font-medium truncate",
                          isDarkMode ? "text-white" : "text-gray-900",
                        )}
                      >
                        {plan.name}
                      </h4>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-xs",
                            getStatusBadgeColor(plan.status),
                          )}
                        >
                          {plan.status.charAt(0).toUpperCase() +
                            plan.status.slice(1)}
                        </Badge>
                        {daysRemaining !== null && daysRemaining > 0 && (
                          <span
                            className={cn(
                              "text-xs",
                              isDarkMode ? "text-gray-400" : "text-gray-600",
                            )}
                          >
                            {daysRemaining}d left
                          </span>
                        )}
                      </div>

                      {/* Inline Quick Stats */}
                      <div className="flex items-center gap-3 mt-2 text-xs">
                        {/* Goals Progress */}
                        {totalGoals > 0 && (
                          <div className="flex items-center gap-1">
                            <Target className="w-3 h-3 text-blue-600" />
                            <span
                              className={
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }
                            >
                              {achievedGoals}/{totalGoals} goals
                            </span>
                          </div>
                        )}

                        {/* Next Appointment */}
                        {plan.followUpAppointments?.length > 0 && (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3 text-green-600" />
                            <span
                              className={
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }
                            >
                              {plan.followUpAppointments.length} appt
                              {plan.followUpAppointments.length !== 1
                                ? "s"
                                : ""}
                            </span>
                          </div>
                        )}

                        {/* Subscription Amount */}
                        {plan.subscriptionPlan && (
                          <div className="flex items-center gap-1">
                            <CreditCard className="w-3 h-3 text-purple-600" />
                            <span
                              className={
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }
                            >
                              £{plan.subscriptionPlan.pricePerSupply.toFixed(2)}
                              /
                              {getBillingFrequencyLabel(
                                plan.subscriptionPlan.billingFrequency,
                              ).toLowerCase()}
                            </span>
                          </div>
                        )}

                        {/* Consultation Count */}
                        {plan.consultationHistory &&
                          plan.consultationHistory.length > 0 && (
                            <div className="flex items-center gap-1">
                              <User className="w-3 h-3 text-orange-600" />
                              <span
                                className={
                                  isDarkMode ? "text-gray-400" : "text-gray-600"
                                }
                              >
                                {plan.consultationHistory.length} consult
                                {plan.consultationHistory.length !== 1
                                  ? "s"
                                  : ""}
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>

                  {/* Edit and Delete Buttons */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingPlan((plan as any).__original || plan);
                        setEditModalOpen(true);
                      }}
                      className={cn(
                        "h-7 w-7 p-0",
                        isDarkMode
                          ? "text-gray-400 hover:text-white"
                          : "text-gray-600 hover:text-gray-900",
                      )}
                      title="Edit treatment plan"
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeletingPlan((plan as any).__original || plan);
                        setDeleteConfirmOpen(true);
                      }}
                      className={cn(
                        "h-7 w-7 p-0",
                        isDarkMode
                          ? "text-gray-400 hover:text-red-400"
                          : "text-gray-600 hover:text-red-600",
                      )}
                      title="Delete treatment plan"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Expanded Content with Tables */}
              {isExpanded && (
                <div
                  className={cn(
                    "border-t px-3 pb-3 pt-2 space-y-2",
                    isDarkMode ? "border-gray-700" : "border-gray-200",
                  )}
                >
                  {/* Plan Overview Table */}
                  <div className="overflow-x-auto">
                    <table
                      className={`w-full text-sm border-collapse ${isDarkMode ? "border-gray-600" : "border-gray-300"}`}
                    >
                      <thead>
                        <tr
                          className={
                            isDarkMode ? "bg-gray-700/50" : "bg-gray-100"
                          }
                        >
                          <th
                            className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                            colSpan={2}
                          >
                            📋 Plan Overview
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          className={
                            isDarkMode
                              ? "hover:bg-gray-700/30"
                              : "hover:bg-gray-50"
                          }
                        >
                          <td
                            className={`border px-2 py-1 font-medium ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                          >
                            Status
                          </td>
                          <td
                            className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                          >
                            <Badge
                              variant="outline"
                              className={cn(
                                "text-xs",
                                getStatusBadgeColor(plan.status),
                              )}
                            >
                              {plan.status.charAt(0).toUpperCase() +
                                plan.status.slice(1)}
                            </Badge>
                          </td>
                        </tr>
                        {plan.startDate && (
                          <tr
                            className={
                              isDarkMode
                                ? "hover:bg-gray-700/30"
                                : "hover:bg-gray-50"
                            }
                          >
                            <td
                              className={`border px-2 py-1 font-medium ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                            >
                              Start Date
                            </td>
                            <td
                              className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                            >
                              {new Date(plan.startDate).toLocaleDateString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                },
                              )}
                            </td>
                          </tr>
                        )}
                        {plan.endDate && (
                          <tr
                            className={
                              isDarkMode
                                ? "hover:bg-gray-700/30"
                                : "hover:bg-gray-50"
                            }
                          >
                            <td
                              className={`border px-2 py-1 font-medium ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                            >
                              End Date
                            </td>
                            <td
                              className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                            >
                              {new Date(plan.endDate).toLocaleDateString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                },
                              )}
                              {daysRemaining !== null && daysRemaining > 0 && (
                                <span className="ml-2 text-xs">
                                  ({daysRemaining}d left)
                                </span>
                              )}
                            </td>
                          </tr>
                        )}
                        {plan.description && (
                          <tr
                            className={
                              isDarkMode
                                ? "hover:bg-gray-700/30"
                                : "hover:bg-gray-50"
                            }
                          >
                            <td
                              className={`border px-2 py-1 font-medium ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                            >
                              Description
                            </td>
                            <td
                              className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                            >
                              {plan.description}
                            </td>
                          </tr>
                        )}
                        {plan.consultationHistory &&
                          plan.consultationHistory.length > 0 && (
                            <tr
                              className={
                                isDarkMode
                                  ? "hover:bg-gray-700/30"
                                  : "hover:bg-gray-50"
                              }
                            >
                              <td
                                className={`border px-2 py-1 font-medium ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                              >
                                Consultations
                              </td>
                              <td
                                className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                              >
                                {plan.consultationHistory.length}
                              </td>
                            </tr>
                          )}
                      </tbody>
                    </table>
                  </div>

                  {/* Goals Table */}
                  {plan.goals && plan.goals.length > 0 && (
                    <div className="overflow-x-auto">
                      <table
                        className={`w-full text-sm border-collapse ${isDarkMode ? "border-gray-600" : "border-gray-300"}`}
                      >
                        <thead>
                          <tr
                            className={
                              isDarkMode ? "bg-gray-700/50" : "bg-gray-100"
                            }
                          >
                            <th
                              className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                              colSpan={5}
                            >
                              🎯 Clinical Goals ({totalGoals})
                            </th>
                          </tr>
                          <tr
                            className={
                              isDarkMode ? "bg-gray-700/30" : "bg-gray-50"
                            }
                          >
                            <th
                              className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                            >
                              Goal
                            </th>
                            <th
                              className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                            >
                              Tracking
                            </th>
                            <th
                              className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                            >
                              Target
                            </th>
                            <th
                              className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                            >
                              Due Date
                            </th>
                            <th
                              className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                            >
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {plan.goals.map((goal) => {
                            const hasVital = !!goal.linkedVitalType;
                            const latestVital =
                              hasVital && vitals.length > 0
                                ? vitals
                                    .filter(
                                      (v) => v.type === goal.linkedVitalType,
                                    )
                                    .sort(
                                      (a, b) =>
                                        new Date(b.timestamp).getTime() -
                                        new Date(a.timestamp).getTime(),
                                    )[0]
                                : null;

                            return (
                              <tr
                                key={goal.id}
                                className={cn(
                                  isDarkMode
                                    ? "hover:bg-gray-700/30"
                                    : "hover:bg-gray-50",
                                  hasVital && onVitalClick && "cursor-pointer",
                                )}
                                onClick={() => {
                                  if (
                                    hasVital &&
                                    onVitalClick &&
                                    goal.linkedVitalType
                                  ) {
                                    onVitalClick(goal.linkedVitalType);
                                  }
                                }}
                              >
                                <td
                                  className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                >
                                  <div className="flex items-center gap-2">
                                    {goal.status === "achieved" ? (
                                      <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0" />
                                    ) : goal.status === "in-progress" ? (
                                      <Circle className="w-3 h-3 text-blue-600 flex-shrink-0" />
                                    ) : (
                                      <Circle className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                    )}
                                    <span>{goal.description}</span>
                                  </div>
                                </td>
                                <td
                                  className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                >
                                  {goal.linkedVitalType ? (
                                    <div className="flex items-center gap-1.5">
                                      <Activity className="w-3 h-3 text-blue-600 flex-shrink-0" />
                                      <span className="font-medium">
                                        {getVitalTypeLabel(
                                          goal.linkedVitalType,
                                        )}
                                      </span>
                                      {latestVital && (
                                        <Badge
                                          variant="outline"
                                          className={cn(
                                            "text-[10px] px-1 py-0 ml-1",
                                            isDarkMode
                                              ? "bg-blue-900/30 text-blue-400 border-blue-700"
                                              : "bg-blue-50 text-blue-700 border-blue-200",
                                          )}
                                        >
                                          Latest: {latestVital.value}
                                          {getVitalTypeUnit(
                                            goal.linkedVitalType,
                                          )}
                                        </Badge>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-xs italic opacity-60">
                                      No vital tracking
                                    </span>
                                  )}
                                </td>
                                <td
                                  className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                >
                                  {goal.targetDirection && (
                                    <span className="mr-0.5">
                                      {getTargetDirectionSymbol(
                                        goal.targetDirection,
                                      )}
                                    </span>
                                  )}
                                  {goal.targetValue}
                                  {goal.linkedVitalType && (
                                    <span className="ml-0.5 text-xs">
                                      {getVitalTypeUnit(goal.linkedVitalType)}
                                    </span>
                                  )}
                                </td>
                                <td
                                  className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                >
                                  {goal.targetDate
                                    ? new Date(
                                        goal.targetDate,
                                      ).toLocaleDateString("en-GB", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                      })
                                    : "No target date"}
                                </td>
                                <td
                                  className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                >
                                  <Badge
                                    className={cn(
                                      "text-xs px-1.5 py-0",
                                      getGoalStatusColor(goal.status),
                                    )}
                                  >
                                    {getGoalStatusLabel(goal.status)}
                                  </Badge>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Subscription Table */}
                  {plan.subscriptionPlan && (
                    <div className="overflow-x-auto">
                      <table
                        className={`w-full text-sm border-collapse ${isDarkMode ? "border-gray-600" : "border-gray-300"}`}
                      >
                        <thead>
                          <tr
                            className={
                              isDarkMode ? "bg-gray-700/50" : "bg-gray-100"
                            }
                          >
                            <th
                              className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                              colSpan={2}
                            >
                              💳 Subscription Plan
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            className={
                              isDarkMode
                                ? "hover:bg-gray-700/30"
                                : "hover:bg-gray-50"
                            }
                          >
                            <td
                              className={`border px-2 py-1 font-medium ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                            >
                              Prescription
                            </td>
                            <td
                              className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                            >
                              {plan.subscriptionPlan.prescriptionName}
                            </td>
                          </tr>
                          <tr
                            className={
                              isDarkMode
                                ? "hover:bg-gray-700/30"
                                : "hover:bg-gray-50"
                            }
                          >
                            <td
                              className={`border px-2 py-1 font-medium ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                            >
                              Price
                            </td>
                            <td
                              className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                            >
                              £{plan.subscriptionPlan.pricePerSupply.toFixed(2)}
                            </td>
                          </tr>
                          <tr
                            className={
                              isDarkMode
                                ? "hover:bg-gray-700/30"
                                : "hover:bg-gray-50"
                            }
                          >
                            <td
                              className={`border px-2 py-1 font-medium ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                            >
                              Billing Frequency
                            </td>
                            <td
                              className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                            >
                              {getBillingFrequencyLabel(
                                plan.subscriptionPlan.billingFrequency,
                              )}
                            </td>
                          </tr>
                          <tr
                            className={
                              isDarkMode
                                ? "hover:bg-gray-700/30"
                                : "hover:bg-gray-50"
                            }
                          >
                            <td
                              className={`border px-2 py-1 font-medium ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                            >
                              Supply Progress
                            </td>
                            <td
                              className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                            >
                              {plan.subscriptionPlan.currentSupply} /{" "}
                              {plan.subscriptionPlan.totalSupplies}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Follow-up Appointments Table */}
                  {(plan.followUpAppointments?.length > 0 ||
                    plan.consultationHistory?.length > 0) &&
                    (() => {
                      const appointmentDetails = getAppointmentDetails(
                        plan.followUpAppointments || [],
                      );
                      // Find consultations that are NOT linked to any appointment
                      const linkedConsultationIds = new Set(
                        appointments
                          .map(
                            (apt: any) =>
                              apt.consultationId || apt.patientConsultaitonId,
                          )
                          .filter(Boolean)
                          .map((id: any) => String(id)),
                      );

                      const unscheduledConsultations = (
                        plan.consultationHistory || []
                      ).filter((consult) => {
                        const consultId = String(
                          (consult as any).consultationId || consult.id || "",
                        );
                        return (
                          consultId && !linkedConsultationIds.has(consultId)
                        );
                      });

                      // Find the current/next appointment if this plan is linked
                      const currentAppointment =
                        linkedTreatmentPlanId === plan.id
                          ? linkedTreatmentPlanAppointmentId
                            ? appointmentDetails.find(
                                (apt) =>
                                  String(apt.id) ===
                                  String(linkedTreatmentPlanAppointmentId),
                              ) || null
                            : (appointmentDetails
                                .filter(
                                  (apt) =>
                                    apt.status !== "Completed" &&
                                    apt.patientConsultaitonId ===
                                      currentConsultationId,
                                )
                                .sort(
                                  (a, b) =>
                                    parseAppointmentDate(a) -
                                    parseAppointmentDate(b),
                                )[0] ?? null)
                          : null;

                      const totalItems =
                        appointmentDetails.length +
                        unscheduledConsultations.length;
                      if (totalItems === 0) return null;

                      return (
                        <div className="overflow-x-auto">
                          <table
                            className={`w-full text-sm border-collapse ${isDarkMode ? "border-gray-600" : "border-gray-300"}`}
                          >
                            <thead>
                              <tr
                                className={
                                  isDarkMode ? "bg-gray-700/50" : "bg-gray-100"
                                }
                              >
                                <th
                                  className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                                  colSpan={4}
                                >
                                  📅 Follow-up Appointments & Consultations (
                                  {totalItems})
                                </th>
                              </tr>
                              <tr
                                className={
                                  isDarkMode ? "bg-gray-700/30" : "bg-gray-50"
                                }
                              >
                                <th
                                  className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                                >
                                  Date
                                </th>
                                <th
                                  className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                                >
                                  Time
                                </th>
                                <th
                                  className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                                >
                                  Type
                                </th>
                                <th
                                  className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                                >
                                  Status
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {appointmentDetails.map((apt: any) => {
                                const isCompleted = apt.status === "Completed";
                                const appointmentConsultationId = (apt as any)
                                  .consultationId
                                  ? (apt as any).consultationId
                                  : (apt as any).patientConsultaitonId;
                                const linkedConsultation =
                                  appointmentConsultationId
                                    ? consultations.find(
                                        (c: any) =>
                                          String(
                                            c.consultationId || c.id || c._id,
                                          ) ===
                                          String(appointmentConsultationId),
                                      )
                                    : null;
                                const canViewConsultation =
                                  isCompleted && linkedConsultation;
                                const isCurrentAppointment =
                                  currentAppointment?.id === apt.id;

                                return (
                                  <tr
                                    key={`apt-${apt.id}`}
                                    className={cn(
                                      isDarkMode
                                        ? "hover:bg-gray-700/30"
                                        : "hover:bg-gray-50",
                                      canViewConsultation && "cursor-pointer",
                                      isCurrentAppointment &&
                                        (isDarkMode
                                          ? "bg-purple-900/20 border-l-4 border-l-purple-500"
                                          : "bg-purple-50 border-l-4 border-l-purple-600"),
                                      isCurrentAppointment && "relative",
                                    )}
                                    onClick={() => {
                                      if (canViewConsultation) {
                                        setSelectedAppointmentConsultation(
                                          linkedConsultation,
                                        );
                                      }
                                    }}
                                  >
                                    <td
                                      className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"} ${isCurrentAppointment && "font-semibold"}`}
                                    >
                                      <div className="flex items-center gap-2">
                                        {isCurrentAppointment && (
                                          <div className="relative flex h-3 w-3">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-3 w-3 bg-purple-500"></span>
                                          </div>
                                        )}
                                        {apt.date}
                                      </div>
                                    </td>
                                    <td
                                      className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"} ${isCurrentAppointment && "font-semibold"}`}
                                    >
                                      {apt.time}
                                    </td>
                                    <td
                                      className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"} ${isCurrentAppointment && "font-semibold"}`}
                                    >
                                      {apt.type}
                                      {isCurrentAppointment && (
                                        <Badge
                                          variant="outline"
                                          className="ml-2 text-[10px] px-1 py-0 bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-700"
                                        >
                                          ACTIVE
                                        </Badge>
                                      )}
                                    </td>
                                    <td
                                      className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                    >
                                      <div className="flex items-center gap-1.5">
                                        <Badge
                                          variant="outline"
                                          className={cn(
                                            "text-xs px-1.5 py-0",
                                            apt.status === "Pending" &&
                                              "bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-700",
                                            apt.status === "Active" &&
                                              "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700",
                                            apt.status === "Completed" &&
                                              "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700",
                                            apt.status === "Rescheduled" &&
                                              "bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-700",
                                          )}
                                        >
                                          {apt.status}
                                        </Badge>
                                        {/* {canViewConsultation && (
                                          <ExternalLink className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                                        )} */}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                              {/* Unscheduled Consultations */}
                              {/* {unscheduledConsultations.map((consult) => (
                                <tr
                                  key={`consult-${consult.id}`}
                                  className={cn(
                                    isDarkMode
                                      ? "hover:bg-gray-700/30 bg-purple-900/10"
                                      : "hover:bg-gray-50 bg-purple-50",
                                    "cursor-pointer",
                                  )}
                                  onClick={() => {
                                    const consultationData: Consultation = {
                                      id:
                                        parseInt(
                                          consult.id.replace(/\D/g, ""),
                                        ) || 0,
                                      date: consult.date,
                                      time: consult.time,
                                      type: consult.type,
                                      pharmacist: consult.pharmacist,
                                      summary: consult.summary || "",
                                      actions: [],
                                    };
                                    setSelectedAppointmentConsultation(
                                      consultationData,
                                    );
                                  }}
                                >
                                  <td
                                    className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                  >
                                    {consult.date}
                                  </td>
                                  <td
                                    className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                  >
                                    {consult.time}
                                  </td>
                                  <td
                                    className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                  >
                                    <div className="flex items-center gap-1.5">
                                      <span>{consult.type}</span>
                                      <Badge
                                        variant="outline"
                                        className="text-[10px] px-1 py-0 bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-700"
                                      >
                                        Unscheduled
                                      </Badge>
                                    </div>
                                  </td>
                                  <td
                                    className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                  >
                                    <div className="flex items-center gap-1.5">
                                      <Badge
                                        variant="outline"
                                        className="text-xs px-1.5 py-0 bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-700"
                                      >
                                        Completed
                                      </Badge>
                                    </div>
                                  </td>
                                </tr>
                              ))} */}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}

                  {onViewDetails && (
                    <div className="pt-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className={cn(
                          "w-full text-xs h-7",
                          isDarkMode
                            ? "text-blue-400 hover:bg-gray-700"
                            : "text-blue-600 hover:bg-gray-100",
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewDetails((plan as any).__original || plan);
                        }}
                      >
                        View Full Details
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Edit Treatment Plan Modal */}
      {editingPlan && (
        <TreatmentPlanModal
          existingPlan={editingPlan}
          editMode={true}
          isDarkMode={isDarkMode}
          open={editModalOpen}
          onOpenChange={(open) => {
            setEditModalOpen(open);
            if (!open) setEditingPlan(null);
          }}
          schedulePharmacists={servicePharmacists} // ← ADD
          scheduleRoomNames={serviceRooms?.map((r: any) => r.name)} // ← ADD
          services={serviceTypesInfo}
          onSave={async () => {
            setEditModalOpen(false);
            setEditingPlan(null);
            if (selectedPatientDetails?.patientID) {
              await getPatientActiveTreatmentPlan(
                selectedPatientDetails.patientID,
              );
            }
          }}
        />
      )}

      {/* Appointment Consultation Details Modal */}
      <Dialog
        open={!!selectedAppointmentConsultation}
        onOpenChange={(open) =>
          !open && setSelectedAppointmentConsultation(null)
        }
      >
        <DialogContent
          className={cn(
            "max-w-2xl",
            isDarkMode ? "bg-gray-800 text-white border-gray-700" : "bg-white",
          )}
        >
          <DialogHeader>
            <DialogTitle className={isDarkMode ? "text-white" : ""}>
              Consultation Details
            </DialogTitle>
            <DialogDescription className={isDarkMode ? "text-gray-400" : ""}>
              {selectedAppointmentConsultation?.date} at{" "}
              {selectedAppointmentConsultation?.time}
            </DialogDescription>
          </DialogHeader>
          {selectedAppointmentConsultation && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p
                    className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Type
                  </p>
                  <p
                    className={`font-medium ${isDarkMode ? "text-gray-300" : "text-gray-900"}`}
                  >
                    {selectedAppointmentConsultation.type}
                  </p>
                </div>
                <div>
                  <p
                    className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Pharmacist
                  </p>
                  <p
                    className={`font-medium ${isDarkMode ? "text-gray-300" : "text-gray-900"}`}
                  >
                    {selectedAppointmentConsultation.pharmacist}
                  </p>
                </div>
              </div>
              {selectedAppointmentConsultation.summary && (
                <div>
                  <p
                    className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-500"} mb-2`}
                  >
                    Summary
                  </p>
                  <p
                    className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                  >
                    {selectedAppointmentConsultation.summary}
                  </p>
                </div>
              )}
              {selectedAppointmentConsultation.actions &&
                selectedAppointmentConsultation.actions.length > 0 && (
                  <div>
                    <p
                      className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-500"} mb-2`}
                    >
                      Actions Taken
                    </p>
                    <div className="space-y-2">
                      {(selectedAppointmentConsultation.actions as any[]).map(
                        (action: any, idx) => (
                          <div
                            key={idx}
                            className={cn(
                              "p-2 rounded-lg border",
                              isDarkMode
                                ? "bg-gray-700/50 border-gray-600"
                                : "bg-gray-50 border-gray-200",
                            )}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <Badge
                                  variant="outline"
                                  className={cn(
                                    "text-xs mb-1",
                                    action.type === "prescription" &&
                                      "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400",
                                    action.type === "payment" &&
                                      "bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400",
                                    action.type === "referral" &&
                                      "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400",
                                    action.type === "vaccine" &&
                                      "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400",
                                    action.type === "appointment" &&
                                      "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400",
                                  )}
                                >
                                  {action.type.charAt(0).toUpperCase() +
                                    action.type.slice(1)}
                                </Badge>
                                <p
                                  className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                >
                                  {action.description}
                                </p>
                              </div>
                              {action.amount && (
                                <p
                                  className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-900"}`}
                                >
                                  {action.amount}
                                </p>
                              )}
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Treatment Plan Confirmation Modal */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent
          className={cn(
            "max-w-sm",
            isDarkMode ? "bg-gray-800 text-white border-gray-700" : "bg-white",
          )}
        >
          <DialogHeader>
            <DialogTitle className={isDarkMode ? "text-white" : ""}>
              Delete Treatment Plan
            </DialogTitle>
            <DialogDescription className={isDarkMode ? "text-gray-400" : ""}>
              Are you sure you want to delete this treatment plan? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {deletingPlan && (
              <div
                className={cn(
                  "p-3 rounded-lg border",
                  isDarkMode
                    ? "bg-gray-700/50 border-gray-600"
                    : "bg-gray-50 border-gray-200",
                )}
              >
                <p
                  className={`font-medium ${isDarkMode ? "text-gray-300" : "text-gray-900"}`}
                >
                  {deletingPlan.name}
                </p>
              </div>
            )}
            <div className="flex gap-2 justify-end pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setDeletingPlan(null);
                }}
                className={isDarkMode ? "border-gray-600" : ""}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={async () => {
                  try {
                    const existingTreatmentPlanId =
                      deletingPlan?.treatmentPlanId || deletingPlan?.id || "";
                    const resolvedConsultationId =
                      deletingPlan?.consultationId || "";
                    const resolvedPatientId =
                      deletingPlan?.patientId ||
                      selectedPatientDetails?.patientID ||
                      "";

                    if (
                      existingTreatmentPlanId &&
                      resolvedConsultationId &&
                      resolvedPatientId
                    ) {
                      await deleteTreatmentPlanAction(
                        existingTreatmentPlanId,
                        resolvedConsultationId,
                        resolvedPatientId,
                      );
                      await getPatientActiveTreatmentPlan(resolvedPatientId);
                    }
                  } finally {
                    setDeleteConfirmOpen(false);
                    setDeletingPlan(null);
                  }
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
